import { useState, useEffect } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { adminAPI } from "@/lib/api";
import {
  Users, CreditCard, TrendingUp, BarChart3, FileText, Target,
  PenTool, ArrowUpRight, RefreshCw, Search, ChevronDown, Award,
  Activity, DollarSign, Percent, Clock, MessageCircle, Bug, Lightbulb, Heart, MessageSquare
} from "lucide-react";
import { toast } from "sonner";

const PLAN_COLORS = {
  free: "bg-slate-100 text-slate-600",
  pro: "bg-[#003087]/10 text-[#003087]",
  premium: "bg-[#00A3A6]/10 text-[#00A3A6]",
};

const TOOL_ICONS = {
  ats: Target,
  nhs_gen: FileText,
  coaching: TrendingUp,
  cv_rewrite: PenTool,
  interview_sim: MessageSquare,
};

const TOOL_LABELS = {
  ats: "ATS Analyser",
  nhs_gen: "NHS SI Generator",
  coaching: "Band Coaching",
  cv_rewrite: "CV Rewriter",
  interview_sim: "Interview Simulation",
};

export default function AdminDashboard() {
  const [costStats, setCostStats] = useState(null);

  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [userTotal, setUserTotal] = useState(0);
  const [stories, setStories] = useState([]);
  const [feedbackItems, setFeedbackItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [planFilter, setPlanFilter] = useState("all");
  const [userPage, setUserPage] = useState(0);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, storiesRes, fbRes, costRes] = await Promise.allSettled([
        adminAPI.stats(),
        adminAPI.users({ skip: 0, limit: 50 }),
        adminAPI.stories(),
        adminAPI.feedback(),
        adminAPI.costStats(),
      ]);

      // Check if the primary request failed with 403 (not admin)
      if (statsRes.status === "rejected") {
        const status = statsRes.reason?.response?.status;
        if (status === 403) {
          toast.error("Admin access required");
          setLoading(false);
          return;
        }
      }

      let failedCount = 0;

      if (statsRes.status === "fulfilled") {
        setStats(statsRes.value.data);
      } else { failedCount++; }

      if (usersRes.status === "fulfilled") {
        setUsers(usersRes.value.data.users || []);
        setUserTotal(usersRes.value.data.total || 0);
      } else { failedCount++; }

      if (storiesRes.status === "fulfilled") {
        setStories(Array.isArray(storiesRes.value.data) ? storiesRes.value.data : []);
      } else { failedCount++; }

      if (fbRes.status === "fulfilled") {
        setFeedbackItems(Array.isArray(fbRes.value.data) ? fbRes.value.data : []);
      } else { failedCount++; }

      if (costRes.status === "fulfilled") {
        setCostStats(costRes.value.data);
      } else { failedCount++; }

      if (failedCount > 0 && failedCount < 5) {
        toast.warning(`Some admin data failed to load (${failedCount}/5). Try refreshing.`);
      } else if (failedCount === 5) {
        toast.error("Failed to load admin data. Please check your connection and try again.");
      }
    } catch (err) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async (skip = 0) => {
    try {
      const params = { skip, limit: 50 };
      if (planFilter !== "all") params.plan = planFilter;
      if (searchQuery) params.search = searchQuery;
      const res = await adminAPI.users(params);
      setUsers(res.data.users);
      setUserTotal(res.data.total);
      setUserPage(skip);
    } catch {
      toast.error("Failed to load users");
    }
  };

  const handleUpdatePlan = async (userId, newPlan) => {
    try {
      await adminAPI.updateUser(userId, { user_id: userId, subscription_plan: newPlan });
      toast.success(`User plan updated to ${newPlan}`);
      loadUsers(userPage);
      loadAll();
    } catch {
      toast.error("Failed to update user");
    }
  };

  const handleApproveStory = async (storyId) => {
    try {
      await adminAPI.approveStory(storyId);
      toast.success("Story approved");
      const res = await adminAPI.stories();
      setStories(res.data);
    } catch {
      toast.error("Failed to approve story");
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-[#003087] animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  const costTools = costStats?.tokens_by_tool || {};
  const costCosts = costStats?.cost_by_tool_usd || {};
  const costCounts = costStats?.request_counts_by_tool || {};
  const modelDistribution = costStats?.model_distribution || {};
  const heavyUsers = costStats?.heavy_users || [];

  const featureData = stats?.feature_usage || {};
  const totalFeatureUse = Object.values(featureData).reduce((a, b) => a + b, 0) || 1;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-dashboard">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#003087]" style={{ fontFamily: "Public Sans" }}>Admin Dashboard</h1>
            <p className="text-slate-500 mt-1">Your command centre for BandBoost AI.</p>
          </div>
          <Button variant="outline" size="sm" onClick={loadAll} data-testid="admin-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-kpi-cards">
          <KPICard icon={Users} label="Total Users" value={stats?.total_users || 0} color="#003087" />
          <KPICard icon={DollarSign} label="MRR" value={`£${(stats?.mrr || 0).toFixed(0)}`} color="#007F3B" />
          <KPICard icon={Percent} label="Conversion" value={`${stats?.conversion_rate || 0}%`} color="#00A3A6" />
          <KPICard icon={Activity} label="Engagement" value={`${stats?.engagement_rate || 0}%`} color="#FFB81C" />
        </div>

        {/* Secondary metrics */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <MetricCard label="Paid Users" value={stats?.paid_users || 0} />
          <MetricCard label="Total Revenue" value={`£${(stats?.total_revenue || 0).toFixed(0)}`} />
          <MetricCard label="Monthly Outputs" value={stats?.monthly_outputs || 0} />
          <MetricCard label="7-Day Active" value={stats?.returning_users_7d || 0} />
          <MetricCard label="Top Tool" value={stats?.top_tool?.label || "—"} />
        </div>

        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="overview" data-testid="admin-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="costs" data-testid="admin-tab-costs">Cost Monitoring</TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
            <TabsTrigger value="feedback" data-testid="admin-tab-feedback">Feedback ({feedbackItems.length})</TabsTrigger>
            <TabsTrigger value="stories" data-testid="admin-tab-stories">Stories</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Plan Distribution */}
              <Card className="bg-white border border-slate-200 shadow-sm rounded-xl" data-testid="admin-plan-dist">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base" style={{ fontFamily: "Public Sans" }}>Plan Distribution</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {["free", "pro", "premium"].map((plan) => {
                    const count = stats?.plan_distribution?.[plan] || 0;
                    const pct = stats?.total_users ? Math.round((count / stats.total_users) * 100) : 0;
                    return (
                      <div key={plan} className="flex items-center gap-3">
                        <span className="text-sm font-medium text-slate-700 w-20 capitalize">{plan}</span>
                        <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${plan === "free" ? "bg-slate-400" : plan === "pro" ? "bg-[#003087]" : "bg-[#00A3A6]"}`}
                            style={{ width: `${Math.max(pct, 2)}%` }}
                          />
                        </div>
                        <span className="text-sm text-slate-600 w-16 text-right">{count} ({pct}%)</span>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>

              {/* Feature Usage */}
              <Card className="bg-white border border-slate-200 shadow-sm rounded-xl" data-testid="admin-feature-usage">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base" style={{ fontFamily: "Public Sans" }}>Feature Usage (All Time)</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {Object.entries(TOOL_LABELS).map(([key, label]) => {
                    const count = featureData[key] || 0;
                    const pct = Math.round((count / totalFeatureUse) * 100);
                    const Icon = TOOL_ICONS[key];
                    return (
                      <div key={key} className="flex items-center gap-3">
                        <Icon className="w-4 h-4 text-slate-500" />
                        <span className="text-sm font-medium text-slate-700 w-28">{label}</span>
                        <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full bg-[#003087] transition-all duration-500" style={{ width: `${Math.max(pct, 2)}%` }} />
                        </div>
                        <span className="text-sm text-slate-600 w-16 text-right">{count}</span>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            </div>

            {/* Recent Transactions */}
            {stats?.recent_transactions?.length > 0 && (
              <Card className="bg-white border border-slate-200 shadow-sm rounded-xl" data-testid="admin-transactions">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base" style={{ fontFamily: "Public Sans" }}>Recent Transactions</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Plan</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {stats.recent_transactions.map((tx, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-sm">{tx.user_id}</TableCell>
                          <TableCell><Badge className={PLAN_COLORS[tx.plan] || PLAN_COLORS.free}>{tx.plan}</Badge></TableCell>
                          <TableCell className="text-sm">&pound;{tx.amount?.toFixed(2)}</TableCell>
                          <TableCell>
                            <Badge className={tx.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}>
                              {tx.payment_status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {tx.created_at ? new Date(tx.created_at).toLocaleDateString("en-GB") : "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="costs" className="space-y-4" data-testid="admin-cost-tab">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="bg-white border border-slate-200 shadow-sm rounded-xl" data-testid="admin-cost-overview">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base" style={{ fontFamily: "Public Sans" }}>Cost Overview ({costStats?.month || "—"})</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Monthly Spend (USD)</span>
                    <span className="font-medium text-slate-900">${(costStats?.monthly_total_spend_usd ?? 0).toFixed(4)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Avg Cost / User (USD)</span>
                    <span className="font-medium text-slate-900">${(costStats?.average_cost_per_user_usd ?? 0).toFixed(4)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Total Tokens</span>
                    <span className="font-medium text-slate-900">{costStats?.total_tokens ?? 0}</span>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-white border border-slate-200 shadow-sm rounded-xl" data-testid="admin-cost-models">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base" style={{ fontFamily: "Public Sans" }}>Model Distribution</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  {Object.keys(modelDistribution).length === 0 ? (
                    <p className="text-slate-500">No model usage recorded this month.</p>
                  ) : (
                    Object.entries(modelDistribution).map(([model, stats]) => (
                      <div key={model} className="flex items-center justify-between">
                        <span className="text-slate-600">{model}</span>
                        <span className="font-medium text-slate-900">{stats.calls} calls</span>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
              <Card className="bg-white border border-slate-200 shadow-sm rounded-xl" data-testid="admin-cost-warning">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base" style={{ fontFamily: "Public Sans" }}>Cost Watch</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm text-slate-600">
                  <p>Monitoring per-tool spend and heavy users for early drift detection.</p>
                  <p>Soft safeguards remain active for interview simulations.</p>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="bg-white border border-slate-200 shadow-sm rounded-xl" data-testid="admin-cost-tools">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base" style={{ fontFamily: "Public Sans" }}>Tokens & Cost by Tool</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tool</TableHead>
                        <TableHead>Tokens</TableHead>
                        <TableHead>Cost (USD)</TableHead>
                        <TableHead>Requests</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Object.keys(costTools).length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center text-slate-500">No usage data available.</TableCell>
                        </TableRow>
                      ) : (
                        Object.entries(costTools).map(([tool, tokens]) => (
                          <TableRow key={tool} data-testid={`admin-cost-tool-${tool}`}>
                            <TableCell className="text-sm">{TOOL_LABELS[tool] || tool}</TableCell>
                            <TableCell className="text-sm">{tokens}</TableCell>
                            <TableCell className="text-sm">${(costCosts[tool] ?? 0).toFixed(4)}</TableCell>
                            <TableCell className="text-sm">{costCounts[tool] ?? 0}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              <Card className="bg-white border border-slate-200 shadow-sm rounded-xl" data-testid="admin-cost-heavy-users">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base" style={{ fontFamily: "Public Sans" }}>Heavy Users (Month)</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Cost (USD)</TableHead>
                        <TableHead>Tokens</TableHead>
                        <TableHead>Requests</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {heavyUsers.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center text-slate-500">No heavy users detected.</TableCell>
                        </TableRow>
                      ) : (
                        heavyUsers.map((hu, index) => (
                          <TableRow key={hu.user_id || index} data-testid={`admin-heavy-user-${index}`}>
                            <TableCell className="text-sm">{hu.email || hu.user_id}</TableCell>
                            <TableCell className="text-sm">${(hu.monthly_cost_usd ?? 0).toFixed(4)}</TableCell>
                            <TableCell className="text-sm">{hu.total_tokens ?? 0}</TableCell>
                            <TableCell className="text-sm">{hu.requests ?? 0}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          </TabsContent>


          <TabsContent value="users" className="space-y-4">
            {/* User Search & Filter */}
            <div className="flex flex-col sm:flex-row gap-3" data-testid="admin-user-controls">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
                <Input
                  placeholder="Search by name or email..."
                  className="pl-9"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && loadUsers(0)}
                  data-testid="admin-user-search"
                />
              </div>
              <Select value={planFilter} onValueChange={(v) => { setPlanFilter(v); }}>
                <SelectTrigger className="w-36" data-testid="admin-plan-filter">
                  <SelectValue placeholder="All Plans" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Plans</SelectItem>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="premium">Premium</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={() => loadUsers(0)} className="bg-[#003087] text-white hover:bg-[#002366]" data-testid="admin-search-btn">
                <Search className="w-4 h-4 mr-1" /> Search
              </Button>
            </div>

            {/* User Table */}
            <Card className="bg-white border border-slate-200 shadow-sm rounded-xl overflow-hidden" data-testid="admin-user-table">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>User</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Total Outputs</TableHead>
                      <TableHead>Monthly Usage</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u, i) => (
                      <TableRow key={i} data-testid={`admin-user-row-${i}`}>
                        <TableCell>
                          <div>
                            <p className="text-sm font-medium text-slate-900">{u.name || "—"}</p>
                            <p className="text-xs text-slate-500">{u.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={PLAN_COLORS[u.subscription_plan] || PLAN_COLORS.free}>
                            {u.subscription_plan || "free"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">{u.total_outputs || 0}</TableCell>
                        <TableCell className="text-xs text-slate-500">
                          ATS:{u.monthly_usage?.ats || 0} NHS:{u.monthly_usage?.nhs_gen || 0}
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString("en-GB") : "—"}
                        </TableCell>
                        <TableCell>
                          <Select
                            value={u.subscription_plan || "free"}
                            onValueChange={(v) => handleUpdatePlan(u.user_id, v)}
                          >
                            <SelectTrigger className="h-8 text-xs w-24" data-testid={`admin-plan-change-${i}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="free">Free</SelectItem>
                              <SelectItem value="pro">Pro</SelectItem>
                              <SelectItem value="premium">Premium</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
            <div className="flex items-center justify-between text-sm text-slate-500">
              <span>Showing {users.length} of {userTotal} users</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={userPage === 0} onClick={() => loadUsers(Math.max(0, userPage - 50))} data-testid="admin-prev-page">Prev</Button>
                <Button variant="outline" size="sm" disabled={userPage + 50 >= userTotal} onClick={() => loadUsers(userPage + 50)} data-testid="admin-next-page">Next</Button>
              </div>
            </div>
          </TabsContent>


          <TabsContent value="feedback" className="space-y-4">
            <Card className="bg-white border border-slate-200 shadow-sm rounded-xl" data-testid="admin-feedback">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: "Public Sans" }}>
                  <MessageCircle className="w-4 h-4 text-[#003087]" /> User Feedback ({feedbackItems.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {feedbackItems.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-8">No feedback received yet.</p>
                ) : (
                  <div className="space-y-3">
                    {feedbackItems.map((fb, i) => {
                      const typeIcons = { bug: Bug, feature: Lightbulb, praise: Heart, general: MessageSquare };
                      const typeColors = { bug: "#D5281B", feature: "#FFB81C", praise: "#007F3B", general: "#003087" };
                      const FbIcon = typeIcons[fb.type] || MessageSquare;
                      return (
                        <div key={i} className="p-4 bg-slate-50 rounded-lg" data-testid={`admin-feedback-${i}`}>
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <FbIcon className="w-4 h-4" style={{ color: typeColors[fb.type] }} />
                              <span className="text-sm font-medium text-slate-900">{fb.user_name || fb.user_email}</span>
                              <Badge variant="outline" className="text-xs">{fb.type}</Badge>
                              <Badge className={PLAN_COLORS[fb.subscription_plan] || PLAN_COLORS.free} variant="outline">{fb.subscription_plan}</Badge>
                              {fb.rating && <span className="text-xs text-slate-500">Rating: {fb.rating}/5</span>}
                            </div>
                            <span className="text-xs text-slate-400">{fb.created_at ? new Date(fb.created_at).toLocaleDateString("en-GB") : ""}</span>
                          </div>
                          <p className="text-sm text-slate-600">{fb.message}</p>
                          {fb.page && <p className="text-xs text-slate-400 mt-1">Page: {fb.page}</p>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="stories" className="space-y-4">
            <Card className="bg-white border border-slate-200 shadow-sm rounded-xl" data-testid="admin-stories">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: "Public Sans" }}>
                  <Award className="w-4 h-4 text-[#FFB81C]" /> Success Stories ({stories.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stories.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-8">No success stories yet.</p>
                ) : (
                  <div className="space-y-3">
                    {stories.map((story, i) => (
                      <div key={i} className="p-4 bg-slate-50 rounded-lg" data-testid={`admin-story-${i}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-slate-900">{story.user_name}</span>
                            <Badge variant="outline">{story.profession}</Badge>
                            <Badge className="bg-[#00A3A6]/10 text-[#00A3A6]">{story.band_achieved}</Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            {story.approved ? (
                              <Badge className="bg-green-100 text-green-700">Approved</Badge>
                            ) : (
                              <Button size="sm" className="bg-[#007F3B] text-white hover:bg-[#006330] h-7 text-xs" onClick={() => handleApproveStory(story.story_id)} data-testid={`approve-story-${i}`}>
                                Approve
                              </Button>
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-slate-600">{story.testimonial}</p>
                        <p className="text-xs text-slate-400 mt-2">
                          {story.created_at ? new Date(story.created_at).toLocaleDateString("en-GB") : ""}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}

function KPICard({ icon: Icon, label, value, color }) {
  return (
    <Card className="bg-white border border-slate-200 shadow-sm rounded-xl">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-2">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}12` }}>
            <Icon className="w-4.5 h-4.5" style={{ color }} />
          </div>
          <ArrowUpRight className="w-4 h-4 text-slate-400" />
        </div>
        <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Public Sans" }}>{value}</p>
        <p className="text-xs text-slate-500 mt-0.5">{label}</p>
      </CardContent>
    </Card>
  );
}

function MetricCard({ label, value }) {
  return (
    <Card className="bg-white border border-slate-200 shadow-sm rounded-xl">
      <CardContent className="p-4 text-center">
        <p className="text-lg font-semibold text-slate-900">{value}</p>
        <p className="text-xs text-slate-500">{label}</p>
      </CardContent>
    </Card>
  );
}
